import type { GameFacts, Phase } from '@chess/api-model';

export const VERDICT_API = 'verdict';

/** What the composer reads of a game: the facts, and the period's average of the stage's measure to set it against. */
export type VerdictFacts = Pick<
  GameFacts,
  | 'outcome'
  | 'ending'
  | 'timeClass'
  | 'opening'
  | 'openingScore'
  | 'stageSwings'
  | 'stageCurves'
  | 'middlegameStart'
  | 'middlegameResult'
  | 'groundCause'
  | 'groundPractice'
  | 'endingType'
  | 'endingCase'
  | 'endingResult'
  | 'timeSlip'
  | 'openingErrors'
  | 'middlegame'
  | 'endgame'
  | 'scanned'
  | 'accuracy'
  | 'blunders'
  | 'misses'
> & {
  /** Pawns: the period's average of the stage's measure (the score after the opening, or the change across the stage); undefined without one. */
  average?: number;
  /** How many games the average is over. */
  averageOf?: number;
  /** The reader's average accuracy over the period's scanned games, for the game verdict. */
  averageAccuracy?: number;
};

/** The stage that decided a game: the one whose score change was the largest, when it was at least `DECIDING_PAWNS`. */
export type DecidingStage = { kind: 'stage'; stage: Phase; pawns: number } | { kind: 'even' } | { kind: 'unscanned' };

export interface StageVerdict {
  /** The sentences joined, for a caption. */
  text: string;
  /** The sentences one by one, for taglines: the result first, then what decided it, then the clock. */
  lines: string[];
  /** Whether the clock had a part in it (lost on time, a win let slip with seconds left, a mistake under a minute). */
  clock: boolean;
}

/** The verdict service: a sentence on a game and on each of its stages, and the stage that decided it. */
export interface Verdict {
  /** The whole game: the result, the accuracy against the usual, the stage that decided it, the clock. */
  game(facts: VerdictFacts): StageVerdict | undefined;
  stage(stage: Phase, facts: VerdictFacts): StageVerdict | undefined;
  /** The stage whose score change was the largest, when it was at least a pawn; "even" otherwise; "unscanned" before the scan. */
  decidingStage(facts: Pick<VerdictFacts, 'scanned' | 'openingScore' | 'stageSwings'>): DecidingStage;
}
